/**
 * CommunityFacilitiesFab - FAB with badge showing community-created facilities
 * 
 * Features:
 * - Small FAB that pops out from the Add Facility FAB
 * - Badge showing count of new facilities since last visit
 * - Opens dialog listing all community facilities
 */

import { useState, useEffect, useCallback } from 'react';
import {
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  Chip,
  Tooltip
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import GroupsIcon from '@mui/icons-material/Groups';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import MapIcon from '@mui/icons-material/Map';
import ShareIcon from '@mui/icons-material/Share';
import GoogleIcon from '@mui/icons-material/Google';
import OilBarrelIcon from '@mui/icons-material/OilBarrel';
import WaterDropIcon from '@mui/icons-material/WaterDrop';
import FactoryIcon from '@mui/icons-material/Factory';
import LocalGasStationIcon from '@mui/icons-material/LocalGasStation';
import PlumbingIcon from '@mui/icons-material/Plumbing';
import BoltIcon from '@mui/icons-material/Bolt';
import { useAppStore } from '../stores/appStore';
import { getCyberColors } from '../theme/cyberColors';
import CyberSpinner from './CyberSpinner';
import { telegram } from '../services/telegram';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';

// Facility type icons and labels
const FACILITY_TYPE_INFO: Record<string, { label: string; color: string }> = {
  'well': { label: 'Well', color: '#22c55e' },
  'swd': { label: 'SWD', color: '#3b82f6' },
  'tank_battery': { label: 'Tank Battery', color: '#f59e0b' },
  'compressor': { label: 'Compressor', color: '#8b5cf6' },
  'gas_plant': { label: 'Gas Plant', color: '#ef4444' },
  'pump_station': { label: 'Pump Station', color: '#06b6d4' },
  'injection_well': { label: 'Injection Well', color: '#ec4899' },
  'other': { label: 'Other', color: '#6b7280' }
};

interface CommunityFacility {
  id: string;
  facility_type: string;
  name: string;
  api_number?: string;
  operator?: string;
  latitude: number;
  longitude: number;
  county?: string;
  state?: string;
  notes?: string;
  photo_url?: string;
  created_at: string;
}

interface CommunityFacilitiesFabProps {
  show?: boolean;
  onNavigate?: (lat: number, lng: number, name: string) => void;
  externalOpen?: boolean;
  onExternalOpenChange?: (open: boolean) => void;
}

// Local storage key for tracking last visit
const LAST_VISIT_KEY = 'community_facilities_last_visit';
const LAST_COUNT_KEY = 'community_facilities_last_count';

// API base URL
const API_BASE = typeof window !== 'undefined' && window.location.hostname === 'localhost'
  ? 'http://localhost:8788'
  : '';

export function CommunityFacilitiesFab({ onNavigate, externalOpen, onExternalOpenChange }: CommunityFacilitiesFabProps) {
  const darkMode = useAppStore((state) => state.darkMode);
  const highContrast = useAppStore((state) => state.highContrast);
  const colors = getCyberColors(darkMode, highContrast);
  const cardGlow = colors.neonBlue || colors.neonCyan || '#60a5fa';
  const defaultState = 'NM';
  
  const [internalOpen, setInternalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [facilities, setFacilities] = useState<CommunityFacility[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  
  // Use external open state if provided, otherwise use internal
  const open = externalOpen !== undefined ? externalOpen : internalOpen;
  const setOpen = (value: boolean) => {
    if (onExternalOpenChange) {
      onExternalOpenChange(value);
    } else {
      setInternalOpen(value);
    }
  };
  
  // Fetch full list when dialog opens
  const fetchFacilities = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE}/api/community-facilities?limit=100`);
      if (response.ok) {
        const data = await response.json();
        const mappedFacilities = (data.facilities || []).map((f: any) => ({
          ...f,
          state: f.state || defaultState,
        }));
        setFacilities(mappedFacilities);
        setTotalCount(data.totalCount || 0);
        
        // Update last visit tracking
        localStorage.setItem(LAST_VISIT_KEY, new Date().toISOString());
        localStorage.setItem(LAST_COUNT_KEY, String(data.totalCount || 0));
      }
    } catch (error) {
      console.error('Failed to fetch community facilities:', error);
    } finally {
      setLoading(false);
    }
  }, []);
  
  // Fetch facilities when externally opened
  useEffect(() => {
    if (externalOpen && facilities.length === 0) {
      fetchFacilities();
    }
  }, [externalOpen, fetchFacilities, facilities.length]);
  
  // Fetch count on mount to show badge
  useEffect(() => {
    const fetchCount = async () => {
      try {
        const response = await fetch(`${API_BASE}/api/community-facilities?limit=1`);
        if (response.ok) {
          const data = await response.json();
          const total = data.totalCount || 0;
          setTotalCount(total);
        }
      } catch (error) {
        console.error('Failed to fetch community facilities count:', error);
      }
    };
    
    fetchCount();
    
    // Refresh count every 5 minutes
    const interval = setInterval(fetchCount, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const handleClose = () => {
    setOpen(false);
  };
  
  const handleNavigate = (facility: CommunityFacility) => {
    telegram.hapticFeedback('light');
    if (onNavigate) {
      onNavigate(facility.latitude, facility.longitude, facility.name);
      handleClose();
    }
  };

  const copyToClipboard = async (text: string) => {
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const textarea = document.createElement('textarea');
        textarea.value = text;
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.focus();
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      }
      telegram.hapticFeedback('success');
    } catch (err) {
      console.error('Copy failed', err);
      telegram.showAlert?.('Unable to copy text');
    }
  };

  const shareFacility = (facility: CommunityFacility) => {
    const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${facility.latitude},${facility.longitude}`;
    const shareText = `${facility.name}\n${facility.api_number ? `API: ${facility.api_number}\n` : ''}${facility.county ? `${facility.county}${facility.state ? `, ${facility.state}` : ''}\n` : ''}${mapsUrl}`;
    telegram.webApp?.openTelegramLink?.(
      `https://t.me/share/url?url=${encodeURIComponent(mapsUrl)}&text=${encodeURIComponent(shareText)}`
    );
    telegram.hapticFeedback('light');
  };

  const openGoogleMaps = (facility: CommunityFacility) => {
    const url = `https://www.google.com/maps/dir/?api=1&destination=${facility.latitude},${facility.longitude}&travelmode=driving`;
    window.open(url, '_blank');
    telegram.hapticFeedback('light');
  };

  const getFacilityInfo = (type: string) => {
    return FACILITY_TYPE_INFO[type] || FACILITY_TYPE_INFO['other'];
  };

  const getFacilityIcon = (type: string, color: string) => {
    const key = (type || 'other').toLowerCase();
    switch (key) {
      case 'swd':
      case 'saltwater disposal':
        return <WaterDropIcon sx={{ color, fontSize: 18 }} />;
      case 'tank_battery':
      case 'tank battery':
        return <FactoryIcon sx={{ color, fontSize: 18 }} />;
      case 'compressor':
        return <BoltIcon sx={{ color, fontSize: 18 }} />;
      case 'gas_plant':
      case 'gas plant':
        return <FactoryIcon sx={{ color, fontSize: 18 }} />;
      case 'pump_station':
      case 'pump station':
        return <LocalGasStationIcon sx={{ color, fontSize: 18 }} />;
      case 'injection_well':
      case 'injection well':
        return <PlumbingIcon sx={{ color, fontSize: 18 }} />;
      case 'well':
        return <OilBarrelIcon sx={{ color, fontSize: 18 }} />;
      default:
        return <LocationOnIcon sx={{ color, fontSize: 18 }} />;
    }
  };

  const renderActionButton = (
    tooltip: string,
    icon: React.ReactNode,
    onClick: (e: React.MouseEvent) => void,
    color: string,
    hoverColor?: string,
    disabled?: boolean
  ) => (
    <Tooltip title={tooltip} arrow>
      <span>
        <IconButton
          size="small"
          disabled={disabled}
          onClick={(e) => {
            e.stopPropagation();
            onClick(e);
          }}
          sx={{
            width: 36,
            height: 36,
            color,
            backgroundColor: `${color}15`,
            opacity: disabled ? 0.4 : 1,
            transition: 'transform 0.18s ease, box-shadow 0.18s ease',
            '&:hover': {
              backgroundColor: `${hoverColor || color}25`,
              transform: disabled ? 'none' : 'translateY(-1px)',
              boxShadow: disabled ? 'none' : `0 6px 12px ${color}30`,
            },
          }}
        >
          {icon}
        </IconButton>
      </span>
    </Tooltip>
  );

  return (
    <>
      {/* Community Facilities Dialog */}
      <Dialog
        open={open}
        onClose={handleClose}
        fullScreen
        sx={{
          '& .MuiDialog-paper': {
            bgcolor: colors.bgPrimary,
            backgroundImage: 'none'
          }
        }}
      >
        <DialogTitle
          sx={{
            background: `linear-gradient(135deg, ${colors.neonCyan}15 0%, ${colors.neonPurple}15 100%)`,
            borderBottom: `1px solid ${colors.borderDefault}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            py: 1.5,
            px: 2
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <GroupsIcon sx={{ color: colors.neonCyan }} />
            <Typography variant="h6" sx={{ fontWeight: 700, color: colors.textPrimary }}>
              Community Facilities
            </Typography>
            <Chip
              label={`${totalCount} total`}
              size="small"
              sx={{
                bgcolor: `${colors.neonCyan}20`,
                color: colors.neonCyan,
                fontWeight: 600,
                fontSize: '0.7rem'
              }}
            />
          </Box>
          <IconButton onClick={handleClose} sx={{ color: colors.textMuted }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        
        <DialogContent sx={{ p: 2, bgcolor: colors.bgPrimary }}>
          {/* Description */}
          <Typography variant="body2" sx={{ color: colors.textMuted, mb: 2 }}>
            Facilities added by the community. Tap any facility to navigate to its location.
          </Typography>
          
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CyberSpinner size={48} />
            </Box>
          ) : facilities.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <OilBarrelIcon sx={{ fontSize: 64, color: colors.textMuted, mb: 2 }} />
              <Typography variant="h6" sx={{ color: colors.textMuted }}>
                No facilities yet
              </Typography>
              <Typography variant="body2" sx={{ color: colors.textMuted }}>
                Be the first to add a facility!
              </Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {facilities.map((facility) => {
                const info = getFacilityInfo(facility.facility_type);
                const displayId = facility.api_number || facility.id;
                return (
                  <Box
                    key={facility.id}
                    sx={{
                      backgroundColor: darkMode ? '#0b1220' : '#ffffff',
                      border: darkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid #e2e8f0',
                      borderRadius: '14px',
                      boxShadow: `0 10px 22px rgba(0,0,0,0.08), 0 0 14px ${cardGlow}25`,
                      px: 1.75,
                      py: 1.25,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 0.5,
                      transition: 'transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: `0 14px 30px rgba(0,0,0,0.12), 0 0 20px ${cardGlow}40`,
                        borderColor: `${info.color}50`,
                      }
                    }}
                  >
                    {/* ROW 1: Icon + Facility Name */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
                      <Box sx={{
                        width: 32,
                        height: 32,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: `${info.color}18`,
                        border: `2px solid ${info.color}60`,
                        flexShrink: 0
                      }}>
                        {getFacilityIcon(facility.facility_type, info.color)}
                      </Box>
                      <Typography 
                        variant="subtitle2" 
                        sx={{ 
                          fontWeight: 700, 
                          color: colors.textPrimary,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          fontSize: '1.08rem',
                          lineHeight: 1.3,
                          flex: 1
                        }}
                      >
                        {facility.name}
                      </Typography>
                    </Box>

                    {/* ROW 2: Chips - API, Coords, County, Operator */}
                    <Box sx={{ 
                      display: 'flex', 
                      gap: 0.75, 
                      flexWrap: 'wrap', 
                      alignItems: 'center',
                      overflow: 'hidden',
                      pb: 0.5,
                    }}>
                      <Chip
                        label={displayId}
                        size="small"
                        icon={<ContentCopyIcon sx={{ fontSize: '0.85rem !important' }} />}
                        onClick={async (e) => {
                          e.stopPropagation();
                          await copyToClipboard(displayId);
                        }}
                        sx={{
                          height: 22,
                          fontSize: '0.75rem',
                          fontWeight: 600,
                          fontFamily: '"Google Sans Flex", system-ui, sans-serif',
                          backgroundColor: darkMode ? '#111827' : '#e0e7ff',
                          color: '#1d4ed8',
                          cursor: 'pointer',
                          '& .MuiChip-icon': { color: '#1d4ed8' },
                          '&:hover': { backgroundColor: darkMode ? '#0b1220' : '#d0deff' }
                        }}
                      />
                      <Chip
                        label={`${facility.latitude.toFixed(4)}, ${facility.longitude.toFixed(4)}`}
                        size="small"
                        icon={<LocationOnIcon sx={{ fontSize: '0.85rem !important', color: '#ea580c' }} />}
                        onClick={async (e) => {
                          e.stopPropagation();
                          await copyToClipboard(`${facility.latitude}, ${facility.longitude}`);
                        }}
                        sx={{
                          height: 22,
                          fontSize: '0.75rem',
                          fontWeight: 600,
                          fontFamily: '"Google Sans Flex", system-ui, sans-serif',
                          backgroundColor: darkMode ? '#1f172a' : '#fde8d6',
                          color: '#b45309',
                          cursor: 'pointer',
                          '&:hover': { backgroundColor: darkMode ? '#2a1f34' : '#f7d8bd' }
                        }}
                      />
                      <Chip
                        label={facility.state || defaultState}
                        size="small"
                        sx={{
                          height: 22,
                          fontSize: '0.75rem',
                          fontWeight: 600,
                          fontFamily: '"Google Sans Flex", system-ui, sans-serif',
                          backgroundColor: darkMode ? 'rgba(96, 165, 250, 0.12)' : 'rgba(37, 99, 235, 0.1)',
                          color: darkMode ? '#93c5fd' : '#2563eb',
                        }}
                      />
                      {facility.county && (
                        <Chip 
                          label={facility.county} 
                          size="small" 
                          sx={{ 
                            backgroundColor: darkMode ? 'rgba(52, 211, 153, 0.1)' : 'rgba(6, 95, 70, 0.1)',
                            color: colors.chipGreen || '#34d399',
                            fontSize: '0.75rem',
                            fontWeight: 600,
                            height: 22,
                            fontFamily: '"Google Sans Flex", system-ui, sans-serif'
                          }}
                        />
                      )}
                      {facility.operator && (
                        <Chip 
                          label={facility.operator} 
                          size="small" 
                          sx={{ 
                            backgroundColor: darkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(100, 116, 139, 0.1)',
                            color: darkMode ? '#ffffff' : '#334155',
                            fontSize: '0.75rem',
                            fontWeight: 600,
                            height: 22,
                            maxWidth: 140,
                            fontFamily: '"Google Sans Flex", system-ui, sans-serif',
                            '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' }
                          }}
                        />
                      )}
                    </Box>

                    {/* ROW 3: Action Buttons */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pt: 0.5 }}>
                      {renderActionButton(
                        'Favorite',
                        <StarBorderIcon sx={{ fontSize: '1rem' }} />,
                        () => telegram.hapticFeedback('light'),
                        '#475569'
                      )}
                      {renderActionButton(
                        'View on map',
                        <MapIcon sx={{ fontSize: '1rem' }} />,
                        () => handleNavigate(facility),
                        '#2563eb'
                      )}
                      {renderActionButton(
                        'Share',
                        <ShareIcon sx={{ fontSize: '1rem' }} />,
                        () => shareFacility(facility),
                        '#16a34a'
                      )}
                      {renderActionButton(
                        'Google Maps',
                        <GoogleIcon sx={{ fontSize: '1rem' }} />,
                        () => openGoogleMaps(facility),
                        '#2563eb'
                      )}
                      {renderActionButton(
                        facility.photo_url ? 'Photos' : 'No photos yet',
                        <PhotoLibraryIcon sx={{ fontSize: '1rem' }} />,
                        () => facility.photo_url && window.open(facility.photo_url, '_blank'),
                        '#a21caf',
                        '#86198f',
                        !facility.photo_url
                      )}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

export default CommunityFacilitiesFab;
